import { z } from 'zod'

const hexColorRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/
const colorValueSchema = z.string().regex(hexColorRegex, 'Deve ser uma cor hexadecimal válida')

// Validation helpers
export const validateHexColor = (color: string): boolean => {
  return hexColorRegex.test(color)
}

export const validateDomain = (domain: string): boolean => {
  return /^[a-z0-9-]+$/.test(domain) && domain.length > 0
}

// Base schemas
export const MediaAssetSchema = z.object({
  url: z.string().url('URL inválida'),
  alt: z.string()
})

export const ButtonStyleSchema = z.object({
  background: colorValueSchema,
  text: colorValueSchema,
  hoverBackground: colorValueSchema.optional(),
  hoverText: colorValueSchema.optional()
})

export const InputStyleSchema = z.object({
  background: colorValueSchema,
  text: colorValueSchema,
  border: colorValueSchema.optional(),
  placeholder: colorValueSchema.optional()
})

export const BackgroundSchema = z.object({
  color: colorValueSchema,
  image: z.string().url().optional(),
  imageFill: z.enum(['cover', 'contain', 'repeat']).optional(),
  imagePosition: z.enum(['static', 'relative', 'absolute', 'sticky', 'fixed']).optional(),
  imageMobile: z.string().url().optional(),
  imageFillMobile: z.enum(['cover', 'contain', 'repeat']).optional(),
  imagePositionMobile: z.enum(['static', 'relative', 'absolute', 'sticky', 'fixed']).optional()
})

// Login Page Schema
export const LoginPageConfigSchema = z.object({
  branding: z.object({
    header: z.object({
      logo: MediaAssetSchema.optional(),
      background: colorValueSchema,
      text: colorValueSchema,
      favicon: z.string().url().optional()
    }),
    form: z.object({
      logo: MediaAssetSchema.optional()
    }).optional()
  }).optional(),
  layout: z.object({
    header: z.object({
      background: colorValueSchema,
      text: colorValueSchema
    }).optional(),
    body: BackgroundSchema.extend({
      title: z.string(),
      description: z.string()
    }).optional(),
    form: z.object({
      background: colorValueSchema,
      footer: z.object({
        background: colorValueSchema,
        text: colorValueSchema,
        registerLink: colorValueSchema
      })
    }).optional(),
    footer: z.object({
      background: colorValueSchema,
      text: colorValueSchema
    }).optional()
  }).optional(),
  components: z.object({
    typography: z.object({
      title: colorValueSchema,
      description: colorValueSchema,
      forgotPassword: colorValueSchema
    }).optional(),
    form: z.object({
      input: InputStyleSchema,
      button: ButtonStyleSchema
    }).optional()
  }).optional(),
  features: z.object({
    parentalControl: z.object({
      enabled: z.boolean()
    }).optional(),
    createPasswordOnFirstAccess: z.object({
      enabled: z.boolean()
    }).optional(),
    logoNavOnly: z.boolean().optional()
  }).optional()
}).partial()

// Home Page Schema
export const HomePageConfigSchema = z.object({
  branding: z.object({
    modelVideo: z.object({
      url: z.string().url(),
      type: z.enum(['mentorfy', 'custom']),
      model: z.string().optional(),
      banners: z.array(z.string().url()).optional(),
    })
  }).optional(),
  layout: z.object({
    header: z.object({
      background: colorValueSchema,
      shadow: colorValueSchema,
      text: colorValueSchema
    }).optional(),
    body: BackgroundSchema.extend({
      title: colorValueSchema,
      warning: colorValueSchema,
      danger: colorValueSchema
    }).optional(),
    footer: z.object({
      background: colorValueSchema,
      text: colorValueSchema,
      nipc: z.string().optional(),
      address: z.string().optional()
    }).optional()
  }).optional(),
  components: z.object({
    banner: z.object({
      title: colorValueSchema,
      description: colorValueSchema,
      button: ButtonStyleSchema,
      image: z.string().url(),
      slideImages: z.array(z.string()).optional(),
      overlay: colorValueSchema,
      video: z.string().url(),
      position: z.enum(['center', 'left', 'right', 'top', 'bottom']),
      fill: z.enum(['contain', 'cover', 'fill', 'none', 'scale-down']),
      containBackdrop: z.enum(['none', 'blur']).optional(),
      fillOverlay: z.enum(['contain', 'cover', 'fill', 'none', 'scale-down']),
      positionOverlay: z.enum(['center', 'left', 'right', 'top', 'bottom']),
      overlayAlignment: z.number(),
      disableGradientShadow: colorValueSchema,
      data: z.object({
        enabled: colorValueSchema,
        title: z.string(),
        description: z.string(),
        actionText: z.string(),
        actionLink: z.string(),
        infoText: z.string(),
        infoLink: z.string()
      }),
      actionButton: z.object({
        text: z.string(),
        background: colorValueSchema
      }),
      infoButton: z.object({
        text: z.string(),
        background: colorValueSchema
      })
    }).optional()
  }).optional(),
  features: z.object({
    calendar: z.object({
      enabled: z.boolean()
    }).optional(),
    notifications: z.object({
      enabled: z.boolean(),
      timeout: z.number()
    }).optional(),
    onboarding: z.object({
      enabled: z.boolean(),
      items: z.array(z.any())
    }).optional(),
    terms: z.object({
      enabled: z.boolean(),
      content: z.string(),
      required: z.boolean(),
      buttonText: z.string().optional(),
      buttonColor: colorValueSchema.optional(),
      buttonTextColor: colorValueSchema.optional()
    }).optional(),
    accessRequest: z.object({
      enabled: z.boolean()
    }).optional(),
    buyButtonBuyHere: z.boolean().optional(),
    community: z.object({
      enabled: z.boolean(),
      url: z.string().optional()
    }).optional()
  }).optional()
}).partial()

// Mentoring Page Schema
export const MentoringPageConfigSchema = z.object({
  layout: z.object({
    header: z.object({
      learningPathBackground: colorValueSchema,
      learningPathBackgroundColor: colorValueSchema
    }).optional(),
    body: z.object({
      featuredColor: colorValueSchema,
      informationColor: colorValueSchema,
      warningColor: colorValueSchema,
      progressColor: colorValueSchema
    }).optional()
  }).optional(),
  components: z.object({
    template: z.any()
  }).optional()
}).partial()

// Register Page Schema
export const RegisterPageConfigSchema = z.object({
  features: z.object({
    form: z.object({
      customFields: z.array(z.any())
    })
  }).optional()
}).partial()

// Main ShowcaseConfiguration Schema
export const ShowcaseConfigurationSchema = z.object({
  title: z.string().min(1, 'Título é obrigatório'),
  description: z.string(),
  login: LoginPageConfigSchema,
  home: HomePageConfigSchema,
  mentoring: MentoringPageConfigSchema,
  register: RegisterPageConfigSchema,
  metadata: z.object({
    version: z.string(),
    lastUpdated: z.string(),
    migratedFromOldStructure: z.boolean().optional()
  }).optional()
})

// Type exports
export type ShowcaseConfiguration = z.infer<typeof ShowcaseConfigurationSchema>
export type LoginPageConfig = z.infer<typeof LoginPageConfigSchema>
export type HomePageConfig = z.infer<typeof HomePageConfigSchema>
export type MentoringPageConfig = z.infer<typeof MentoringPageConfigSchema>
export type RegisterPageConfig = z.infer<typeof RegisterPageConfigSchema>
